import React, { useState, useEffect } from 'react';
import { useUserProfile } from "../../../hooks/useUserProfile";
import QRCode from "react-qr-code";
import { Button, Modal } from 'react-daisyui';
import { ExternalLink } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../../lib/supabase';
import { useNavigate } from 'react-router-dom';
import routes from '../../../modules/routes/Routes';


export const useIsTelegramRegistrationValid = () => {
    const { telegramRegistered } = useUserProfile();
    return telegramRegistered;
}

const TelegramRegistration: React.FC = () => {
    const { telegramToken, telegramRegistered, updateTelegramRegistration, subscriptionData } = useUserProfile();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isChecking, setIsChecking] = useState(false);
    const { t } = useTranslation();
    const navigate = useNavigate();

    useEffect(() => {
        const checkInterval = setInterval( async () => {
            if (!telegramRegistered) {
                setIsChecking(true);
                await updateTelegramRegistration().finally(() => setIsChecking(false));
            } else {
                clearInterval(checkInterval);
            }
        }, 1500); // Check every 1.5 seconds

        return () => clearInterval(checkInterval);
    }, [telegramRegistered, updateTelegramRegistration]);

    const TelegramContent: React.FC = () => {
        return (
            <div className="flex flex-col gap-4">
                <p>{t('profilePages.telegramRegistration.instructions')}</p>

                <div className="flex justify-center">
                    <a href={`https://t.me/FindMeinZuhauseBot?start=${telegramToken}`} target="_blank" rel="noopener noreferrer">
                        <Button color="primary" variant="outline">
                            <ExternalLink className="h-5 w-5 mr-2" />
                            {t('profilePages.telegramRegistration.openTelegramBot')}
                        </Button>
                    </a>
                </div>

                <p>{t('profilePages.telegramRegistration.scanQRCode')}</p>

                <div className="flex justify-center">
                    <QRCode
                        value={`https://t.me/FindMeinZuhauseBot?start=${telegramToken}`}
                        size={256}
                    />
                </div>

                {!telegramRegistered && (
                    <div className="flex items-center gap-2">
                    <p>{t('profilePages.telegramRegistration.waitingForCompletion')}</p>
                    <span className="loading loading-spinner loading-md"></span>
                </div>
                )}
            </div>
        );
    };

    if (!subscriptionData?.isSubscribed) {
        return (
            <div className="container mx-auto px-4">
                <h1 className="text-3xl font-bold mb-6">{t('profilePages.telegramRegistration.title')}</h1>
                <div className="flex flex-col gap-4">
                    <p className="mb-4">Once telegram is connected, you will receive notifications whenever we find a matching flat.<br/><br/>To register with Telegram, an active subscription is required.</p>
                    <Button 
                        color="primary" 
                        onClick={() => navigate(routes.app.profile.subscriptionManagement.build())}
                        className="w-full"
                    >
                        Go to Subscription Management
                    </Button>
                </div>
            </div>
        );
    }

    return (
        <div className="container mx-auto px-4">
            <h1 className="text-3xl font-bold mb-6">{t('profilePages.telegramRegistration.title')}</h1>
            <div className="flex flex-col gap-4">
                {telegramRegistered ? (
                    <>
                        <h2 className="text-2xl font-bold text-center text-success">
                            {t('profilePages.telegramRegistration.successMessage')}
                        </h2>
                        <p>{t('profilePages.telegramRegistration.connectDifferentAccount')}</p>

                        <Button color="secondary" onClick={() => setIsModalOpen(true)}>
                            {t('profilePages.telegramRegistration.connectDifferentAccountButton')}
                        </Button>
                        <Modal open={isModalOpen}>
                            <Modal.Header className="font-bold">{t('profilePages.telegramRegistration.modalTitle')}</Modal.Header>
                            <Modal.Body>
                                <TelegramContent />
                            </Modal.Body>
                            <Modal.Actions>
                                <Button color="ghost" onClick={() => setIsModalOpen(false)}>{t('profilePages.telegramRegistration.closeButton')}</Button>
                            </Modal.Actions>
                        </Modal>
                    </>
                ) : (
                    <TelegramContent />
                )}
            </div>
        </div>
    );
};

export default TelegramRegistration;
